import React, { useEffect, useState } from 'react';
import { useSnapshot } from 'valtio';

import { eventsStore, datasetStore, videoStore, spacecraftStore, uiStore, appStore, layersStore, poiStore,gmapStore,nisarStore,stellarStore } from '../managers/globalState';
import globalRefs from '../managers/globalRefs';

import { cloneDate, formatDate, subtractDaysFromDate } from '../helpers/tools';

import '../../www/assets/css/date_label.css';
import DatasetTools from './dataset_tools';
import ar from '../languages/ar'

// Days back for each average type.
const DAYS_BACK_KEYMAP = {
	daily: 0,
	month: 30,
	ten_day: 9,
	seven_day: 6,
	eight_day: 7,
	three_day: 2,
	twelve_day: 11,
	sixteen_day: 15
};

/**
 * Date label component.
 */
const DateLabel = ({ setIsDateLabelLoaded = null }) => {
	const { controlsMenu, isDetailPanelOpen, isDateTooltipOpen } = useSnapshot(uiStore.state);
	const { validData } = useSnapshot(uiStore.state);
	const { eventsVisibleOnGlobe, currentEvent } = useSnapshot(eventsStore.state);
	const { animationDates, currentDataset, currentIndex, datasetHasAnimation } = useSnapshot(datasetStore.state);
	const { isSatellitesNow, currentSpacecraft } = useSnapshot(spacecraftStore.state);
	const { isVideoTextureLoaded } = useSnapshot(videoStore.state);
	const { queries, isMobile } = useSnapshot(appStore.state);
  const { earthquakes } = layersStore.stateSnapshot;

// Show legacy date label if hideUI is true, for EIC displays
	const { hideUI } = queries || {};
	const showUI = hideUI !== 'true' && !earthquakes;
	const { getManager } = globalRefs;
	const { animations } = getManager('dataset');
	const [animEndDate, setAnimEndDate] = useState(animations?.[currentIndex]);

	const { average } = currentDataset || {};
  const { currentPoi } = useSnapshot(poiStore.state);
  const { currentGmap } = useSnapshot(gmapStore.state);
  const { currentNisar } = useSnapshot(nisarStore.state);
  const { currentStellar } = useSnapshot(stellarStore.state);

	// Determine if we should show the date label.
	const showDateLabel = animEndDate && !isSatellitesNow && !currentSpacecraft && !eventsVisibleOnGlobe && !isVideoTextureLoaded && !currentEvent;

	useEffect(() => {
		setIsDateLabelLoaded && setIsDateLabelLoaded(showDateLabel);
	}, [showDateLabel]);

	// Update label when dates change
	useEffect(() => {
		const dateSource = animations?.[currentIndex] ?? currentDataset?.latestTileset;
		const { dateObject: endDateObj } = dateSource || {};

		setAnimEndDate(endDateObj);
	}, [animations, animationDates, currentIndex, currentDataset]);

	useEffect(() => () => {
		uiStore.setGlobalState({ isDateTooltipOpen: false });
	}, [
		animations,
		controlsMenu,
		currentEvent,
		currentSpacecraft,
		isDetailPanelOpen,
		isSatellitesNow
	]);

	const handleTooltipExpand = e => {
		if (isMobile) {
			if (e && e.stopPropagation) { e.stopPropagation(); }
			uiStore.setGlobalState({ isDateTooltipOpen: !isDateTooltipOpen });
		}
	};

	if (!showDateLabel) {
		return null;
	}

	const endDate = cloneDate(animEndDate);
	const daysBack = DAYS_BACK_KEYMAP[average];
	const showRange = average !== 'daily' && average !== 'month';

	/**
	 * Use the spread operator inside the object to conditionally add the day: undefined.
	 * https://www.codemzy.com/blog/conditionally-add-property-to-object-javascript
	*/
	const endDateStr = formatDate(endDate, {
		month: showRange ? 'short' : 'long',
		...average === 'month' && { day: undefined }
	});

	// Dates are stored in UTC ISO format but we want to display them to users in their own timezone.
	// The formatDate tool helper does this for us.
	let beginDateStr = '';
	if (showRange) {
		const beginDate = subtractDaysFromDate(cloneDate(endDate), daysBack);
		beginDateStr = formatDate(beginDate, { month: 'short' });
	}


	const disableContents = controlsMenu === 'time' || controlsMenu === 'settings';
	const dateStr = (showRange ? beginDateStr + ' - ' : '') + endDateStr;

	return (
		showUI
			? (
				<div id='date-label-container' onClick={handleTooltipExpand} className={`${disableContents ? 'disabled' : ''}`}>
					<div className='align-text-mobile'>
						<span className='date-observed'>
							{validData
								? (
                  isMobile ? ar.observed : ar.data_observed
								)
								: (
									ar.data_unavailable
								)}
							{!(isMobile && showRange) && validData && (showRange ? ' ' : ' ')}
						</span>
						{ validData && (
							<div className='date-text outline'>
								{dateStr}
								{!datasetHasAnimation && <div className={`date-text-tooltip ${isMobile && isDateTooltipOpen ? 'expanded' : ''} ${currentDataset?.calendarType === 'singleDay' ? 'singleDate' : ''}`} />}
							</div>
						)}
					</div>
					{isMobile && isDateTooltipOpen && <div className='tooltip-content-mobile'>{ar.date_tooltip}</div>}

					{!isMobile && <DatasetTools />}
				</div>
			)
			: earthquakes === false ? (
				<div id='legacy-date-label-container'>
					<div className='legacy-date-text'>
						{validData ? dateStr : 'Data Temporarily Unavailable'}
					</div>
				</div>
			) : null


	);
};

export default DateLabel;
